/**
 * Demo API Slice
 *
 * RTK Query endpoints for the public "Try It" demo experience.
 * No authentication required.
 */

import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';

const API_URL = import.meta.env.VITE_API_URL || '/api/v1';

// ============================================
// Types
// ============================================

export interface DemoScenarioDto {
  id: string;
  name: string;
  description: string;
  prompt: string;
  category: string;
  difficulty: 'beginner' | 'intermediate' | 'advanced';
}

export interface DemoPracticeTextRequest {
  mode: 'text';
  answer: string;
  scenarioId?: string;
}

/**
 * The demo does not assess (evidence class UNAVAILABLE, reason
 * 'demo_not_assessed'). It carries no score, rating or count — the server
 * used to send word-count "scores" that nothing had measured.
 */
export interface DemoNotAssessed {
  status: 'not_assessed';
  evidenceClass: 'UNAVAILABLE';
  reason: 'demo_not_assessed';
  explanation: string;
}

export interface DemoPracticeResponse {
  transcript: string;
  /** The scenario answered, when it is an active public one; otherwise null. */
  demoScenario: {
    name: string;
    description: string;
  } | null;
  assessment: DemoNotAssessed;
}

interface ApiResponse<T> {
  success: boolean;
  data: T;
  error?: string;
}

// ============================================
// API Slice
// ============================================

export const demoApi = createApi({
  reducerPath: 'demoApi',
  baseQuery: fetchBaseQuery({
    baseUrl: API_URL,
    // No auth headers needed for demo endpoints
  }),
  endpoints: (builder) => ({
    /**
     * GET /api/demo/scenarios
     * Fetch available demo scenarios
     */
    getDemoScenarios: builder.query<DemoScenarioDto[], void>({
      query: () => '/demo/scenarios',
      transformResponse: (response: ApiResponse<DemoScenarioDto[]>) => response.data,
    }),

    /**
     * POST /api/demo/practice (text mode)
     * Submit a text-based demo practice
     */
    runDemoPractice: builder.mutation<DemoPracticeResponse, DemoPracticeTextRequest>({
      query: (body) => ({
        url: '/demo/practice',
        method: 'POST',
        body,
      }),
      transformResponse: (response: ApiResponse<DemoPracticeResponse>) => response.data,
    }),
    // No voice mode: DEMO VOICE is retired (owner decision). The demo is text
    // only; voice is the canonical conversation after sign-in.
  }),
});

export const {
  useGetDemoScenariosQuery,
  useRunDemoPracticeMutation,
} = demoApi;

export default demoApi;
